import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import RequirePermission from "../../../auth/RequirePermission";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import {
  addTrustSafetyCaseNote,
  dismissTrustSafetyCase,
  escalateTrustSafetyCase,
  fetchTrustSafetyCase,
  pauseListingFromCase,
  resolveTrustSafetyCase,
  suspendDealerFromCase,
  updateTrustSafetyCase,
} from "../../../services/automapTrustSafetyApi";
import { trackTrustSafetyEvent } from "../../../constants/automapTrustSafetyConstants";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import {
  CASE_STATUS_LABELS,
  CATEGORY_LABELS,
  ConfirmDialog,
  RESOLUTION_LABELS,
  SEVERITY_CLASS,
  SEVERITY_LABELS,
  formatDate,
} from "./trustSafetyShared";

function AdminTrustSafetyCaseDetailPageContent() {
  const { caseId } = useParams();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);
  const [caseRecord, setCaseRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [resolveForm, setResolveForm] = useState({ resolution: "no_issue_found", resolutionNote: "" });

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchTrustSafetyCase(caseId);
      setCaseRecord(data.case);
      trackTrustSafetyEvent("trust_case_opened", { caseId });
    } catch (err) {
      setError(err.message || "Caso no encontrado.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [caseId]);

  const runAction = async (fn) => {
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err.message || "Acción fallida.");
    }
    setConfirm(null);
  };

  if (loading) return <AdminPageLayout><AdminStatePanel variant="empty" message="Cargando caso…" /></AdminPageLayout>;
  if (!caseRecord) return <AdminPageLayout><AdminStatePanel variant="empty" message={error || "Caso no encontrado."} /></AdminPageLayout>;

  return (
    <AdminPageLayout>
      <AdminAutomotiveNav active="trust-safety" />
      <AdminPageHeader
        eyebrow="AutoMapa · Caso"
        title={caseRecord.title}
        primaryActions={<Link className="admin-btn" to="/admin/trust-safety/cases">Volver</Link>}
      />
      <main className="admin-console__main">
        {error ? <AdminStatePanel variant="empty" message={error} /> : null}
        <div className="admin-listing-detail-grid">
          <section className="admin-dashboard-card">
            <h3>Resumen</h3>
            <p>{caseRecord.summary || "—"}</p>
            <p>Estado: {CASE_STATUS_LABELS[caseRecord.status]}</p>
            <p>Severidad: <span className={`admin-badge ${SEVERITY_CLASS[caseRecord.severity]}`}>{SEVERITY_LABELS[caseRecord.severity]}</span></p>
            {canManage ? (
              <div className="dealer-inventory-actions" style={{ flexWrap: "wrap" }}>
                <button type="button" onClick={() => runAction(() => updateTrustSafetyCase(caseId, { status: "investigating" }))}>Investigar</button>
                <button type="button" onClick={() => runAction(() => updateTrustSafetyCase(caseId, { status: "waiting_on_dealer" }))}>Esperar dealer</button>
                <button type="button" onClick={() => runAction(() => pauseListingFromCase(caseId))}>Pausar listing</button>
                <button type="button" onClick={() => setConfirm("suspend")}>Suspender dealer</button>
                <button type="button" onClick={() => setConfirm("escalate")}>Escalar</button>
                <button type="button" onClick={() => setConfirm("dismiss")}>Descartar</button>
              </div>
            ) : null}
          </section>

          {caseRecord.listing ? (
            <section className="admin-dashboard-card">
              <h3>Listing</h3>
              <p>{caseRecord.listing.title}</p>
              <Link to={`/admin/listings/${caseRecord.listing.id}`}>Abrir listing</Link>
            </section>
          ) : null}

          {caseRecord.dealer ? (
            <section className="admin-dashboard-card">
              <h3>Dealer</h3>
              <p>{caseRecord.dealer.businessName}</p>
              {caseRecord.dealer.verificationStatus ? (
                <p>Verificación: {caseRecord.dealer.verificationStatus}</p>
              ) : null}
              {caseRecord.dealer.verificationStatus === "verified" &&
              ["high", "critical"].includes(caseRecord.severity) ? (
                <p><span className="admin-badge admin-badge--warning">Caso grave abierto con dealer verificado</span></p>
              ) : null}
              {caseRecord.dealer.mainWhatsapp ? (
                <a href={`https://wa.me/${caseRecord.dealer.mainWhatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer">WhatsApp</a>
              ) : null}
              <Link to={`/admin/dealers/${caseRecord.dealer.id}`}>Abrir dealer</Link>
            </section>
          ) : null}

          <section className="admin-dashboard-card">
            <h3>Reportes ({caseRecord.reports?.length || 0})</h3>
            <ul>
              {(caseRecord.reports || []).map((r) => (
                <li key={r.id}>
                  {CATEGORY_LABELS[r.category] || r.category} — {r.description?.slice(0, 80)}
                  {r.buyerWhatsapp ? ` · ${r.buyerWhatsapp}` : ""}
                </li>
              ))}
            </ul>
          </section>

          <section className="admin-dashboard-card">
            <h3>Notas</h3>
            <ul className="admin-activity-list">
              {(caseRecord.notes || []).map((n) => (
                <li key={n.id}><p>{n.body}</p><span className="muted">{formatDate(n.createdAt)}</span></li>
              ))}
            </ul>
            {canManage ? (
              <>
                <textarea value={noteBody} onChange={(e) => setNoteBody(e.target.value)} rows={3} style={{ width: "100%" }} />
                <button type="button" className="admin-btn" onClick={() => runAction(async () => {
                  await addTrustSafetyCaseNote(caseId, { body: noteBody, noteType: "investigation" });
                  setNoteBody("");
                  trackTrustSafetyEvent("trust_case_note_added", { caseId });
                })}>Agregar nota</button>
              </>
            ) : null}
          </section>

          <section className="admin-dashboard-card">
            <h3>Timeline</h3>
            <ul className="admin-activity-list">
              {[...(caseRecord.events || [])].reverse().map((e) => (
                <li key={e.id}><strong>{e.eventType}</strong><p>{e.description}</p><span className="muted">{formatDate(e.createdAt)}</span></li>
              ))}
            </ul>
          </section>

          {canManage ? (
            <section className="admin-dashboard-card">
              <h3>Resolver caso</h3>
              <select value={resolveForm.resolution} onChange={(e) => setResolveForm({ ...resolveForm, resolution: e.target.value })}>
                {Object.entries(RESOLUTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <textarea value={resolveForm.resolutionNote} onChange={(e) => setResolveForm({ ...resolveForm, resolutionNote: e.target.value })} rows={3} style={{ width: "100%", marginTop: 8 }} placeholder="Nota de resolución *" />
              <button type="button" className="admin-btn admin-btn--primary" style={{ marginTop: 8 }} onClick={() => runAction(async () => {
                await resolveTrustSafetyCase(caseId, resolveForm);
                trackTrustSafetyEvent("trust_case_resolved", { caseId });
              })}>Resolver</button>
            </section>
          ) : null}
        </div>
      </main>

      <ConfirmDialog
        open={Boolean(confirm)}
        title="Confirmar acción"
        message={
          confirm === "suspend"
            ? "¿Suspender concesionario? Esta acción requiere confirmación."
            : confirm === "dismiss"
              ? "¿Descartar caso?"
              : "¿Escalar caso?"
        }
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm === "suspend") runAction(() => suspendDealerFromCase(caseId, "Suspendido desde caso de confianza"));
          if (confirm === "dismiss") runAction(() => dismissTrustSafetyCase(caseId, "Descartado por operador"));
          if (confirm === "escalate") runAction(() => escalateTrustSafetyCase(caseId, "Escalado"));
        }}
      />
    </AdminPageLayout>
  );
}

export default function AdminTrustSafetyCaseDetailPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminTrustSafetyCaseDetailPageContent />
    </RequirePermission>
  );
}
